<template>
    <ConfirmDialog></ConfirmDialog>
    <section class="mb-4 px-2 sm:px-4 md:px-6 lg:px-8 pt-2">
        <!-- Main Summary Table -->
        <div v-show="!showClientDetails">
            <div class="mb-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <h1 class="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100">Gestión de Adeudos</h1>
                <span class="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">
                    Total de clientes con adeudo: <strong>{{ totalClients }}</strong>
                </span>
            </div>

            <div v-if="loadingClients" class="text-center py-8">
                <i class="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                <p class="mt-2 text-zinc-600 dark:text-zinc-400">Cargando clientes...</p>
            </div>

            <div v-else class="table-responsive-container">
                <DataTable
                    :value="clients"
                    v-model:filters="filters"
                    stripedRows
                    responsiveLayout="scroll"
                    class="debts-summary-table"
                    :rows="10"
                    :paginator="true"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    :rowsPerPageOptions="[10,20,50]"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} clientes"
                    :globalFilterFields="['name', 'id']"
                >
                    <template #header>
                        <div class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
                            <span></span>
                            <InputText
                                v-model="filters['global'].value"
                                placeholder="Buscar cliente..."
                                class="w-full sm:w-80"
                            />
                        </div>
                    </template>

                    <Column field="id" header="ID" :sortable="true" style="width: 80px">
                        <template #body="{data}">
                            <span class="font-semibold">{{ data.id }}</span>
                        </template>
                    </Column>

                    <Column field="name" header="Cliente" :sortable="true">
                        <template #body="{data}">
                            <div class="font-semibold">{{ data.name }}</div>
                        </template>
                    </Column>

                    <Column field="invoices_count" :sortable="true" style="width: 72px">
                        <template #header>
                            <span v-tooltip.top="'Facturas pendientes'">Fact.</span>
                        </template>
                        <template #body="{data}">
                            <span class="text-red-600 dark:text-red-400 font-bold">
                                {{ data.invoices_count }}
                            </span>
                        </template>
                    </Column>

                    <Column field="total_debt_raw" header="Monto Adeudo" :sortable="true" style="width: 150px">
                        <template #body="{data}">
                            <span class="text-red-600 dark:text-red-400 font-bold text-lg">
                                {{ data.total_debt }}
                            </span>
                        </template>
                    </Column>

                    <Column field="emails_sent_count" :sortable="true" style="width: 88px">
                        <template #header>
                            <span v-tooltip.top="'Correos de cobro enviados'">Enviados</span>
                        </template>
                        <template #body="{data}">
                            <span class="text-sm">{{ data.emails_sent_count }}</span>
                        </template>
                    </Column>

                    <Column field="last_email_sent" :sortable="true" style="width: 110px">
                        <template #header>
                            <span v-tooltip.top="'Fecha del último correo enviado'">Último env.</span>
                        </template>
                        <template #body="{data}">
                            <span class="text-sm text-zinc-600 dark:text-zinc-400">
                                {{ data.last_email_sent ? formatDate(data.last_email_sent) : 'Nunca' }}
                            </span>
                        </template>
                    </Column>

                    <Column field="emails" header="Correos" style="width: 200px">
                        <template #body="{data}">
                            <div class="text-xs sm:text-sm">
                                <div v-if="data.emails.length === 0" class="text-zinc-400 dark:text-zinc-500 italic">Sin correos</div>
                                <div v-else class="space-y-1">
                                    <div v-for="(email, idx) in data.emails" :key="idx" class="truncate" :title="email">
                                        <i class="pi pi-at text-blue-500 text-xs mr-1"></i>
                                        <span class="text-zinc-700 dark:text-zinc-300">{{ email }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Column>

                    <Column field="phones" header="Teléfonos" style="width: 180px">
                        <template #body="{data}">
                            <div class="text-xs sm:text-sm">
                                <div v-if="data.phones.length === 0" class="text-zinc-400 dark:text-zinc-500 italic">Sin teléfonos</div>
                                <div v-else class="space-y-1">
                                    <div v-for="(phone, idx) in data.phones" :key="idx" class="truncate" :title="phone">
                                        <i class="pi pi-mobile text-green-500 text-xs mr-1"></i>
                                        <span class="text-zinc-700 dark:text-zinc-300">{{ phone }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Column>

                    <Column header="Acciones" style="width: 180px">
                        <template #body="{data}">
                            <div class="flex gap-2 justify-end">
                                <Button
                                    icon="pi pi-eye"
                                    class="p-button-rounded p-button-text p-button-sm"
                                    @click="viewClientDetails(data)"
                                    v-tooltip.top="'Ver detalles'"
                                />
                                <Button
                                    v-if="data.emails.length > 0"
                                    icon="pi pi-envelope"
                                    class="p-button-rounded p-button-text p-button-sm p-button-info"
                                    @click="confirmSendEmail(data)"
                                    v-tooltip.top="'Enviar correo'"
                                    :disabled="sendingEmails[data.id]"
                                    :loading="sendingEmails[data.id]"
                                />
                                <Button
                                    v-if="data.phones.length > 0"
                                    icon="pi pi-whatsapp"
                                    class="p-button-rounded p-button-text p-button-sm p-button-success"
                                    @click="prepareWhatsAppMessage(data)"
                                    v-tooltip.top="'WhatsApp'"
                                />
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <!-- Client Details Dialog -->
        <Dialog 
            v-model:visible="showClientDetails" 
            modal 
            :header="`Detalles de Adeudos: ${selectedClient?.name || ''}`" 
            :style="{ width: '90vw', maxWidth: '1200px' }"
            :dismissableMask="true"
            @hide="clearSelection"
        >
            <div v-if="loadingDetails" class="text-center py-8">
                <i class="pi pi-spin pi-spinner text-4xl text-blue-500"></i>
                <p class="mt-2 text-zinc-600 dark:text-zinc-400">Cargando detalles...</p>
            </div>
            <div v-else-if="clientDetails">
                <!-- Invoices Section -->
                <div class="mb-6">
                    <div class="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 mb-4">
                        <div class="flex items-center gap-2 mb-3">
                            <i class="pi pi-file text-blue-600 dark:text-blue-400 text-xl"></i>
                            <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-100">Facturas Pendientes</h3>
                        </div>

                        <div class="grid grid-cols-2 gap-4 p-3 bg-white dark:bg-zinc-800 rounded-lg mb-3">
                            <div>
                                <p class="text-sm text-zinc-600 dark:text-zinc-400">Total de Facturas</p>
                                <p class="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{{ clientDetails.summary.total_invoices }}</p>
                            </div>
                            <div>
                                <p class="text-sm text-zinc-600 dark:text-zinc-400">Monto Total Pendiente</p>
                                <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ clientDetails.summary.total_debt }}</p>
                            </div>
                        </div>

                        <div v-if="clientDetails.invoices.length > 0" class="overflow-x-auto">
                            <table class="w-full text-sm">
                                <thead class="bg-zinc-100 dark:bg-zinc-700">
                                    <tr>
                                        <th class="px-3 py-2 text-left text-zinc-700 dark:text-zinc-300">Folio</th>
                                        <th class="px-3 py-2 text-left text-zinc-700 dark:text-zinc-300">Descripción</th>
                                        <th class="px-3 py-2 text-left text-zinc-700 dark:text-zinc-300">Período</th>
                                        <th class="px-3 py-2 text-right text-zinc-700 dark:text-zinc-300">Total</th>
                                        <th class="px-3 py-2 text-right text-zinc-700 dark:text-zinc-300">Pagado</th>
                                        <th class="px-3 py-2 text-right text-zinc-700 dark:text-zinc-300">Pendiente</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-zinc-200 dark:divide-zinc-700">
                                    <tr v-for="invoice in clientDetails.invoices" :key="invoice.id" class="hover:bg-zinc-50 dark:hover:bg-zinc-800">
                                        <td class="px-3 py-2 text-zinc-900 dark:text-zinc-100 font-semibold">{{ invoice.serial }}</td>
                                        <td class="px-3 py-2 text-zinc-700 dark:text-zinc-300">{{ invoice.description }}</td>
                                        <td class="px-3 py-2 text-zinc-700 dark:text-zinc-300">{{ invoice.invoice_period }}</td>
                                        <td class="px-3 py-2 text-right text-zinc-900 dark:text-zinc-100">{{ invoice.total_amount }}</td>
                                        <td class="px-3 py-2 text-right text-green-600 dark:text-green-400">{{ invoice.total_paid }}</td>
                                        <td class="px-3 py-2 text-right text-red-600 dark:text-red-400 font-semibold">{{ invoice.pending_amount }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="text-center py-3">
                            <i class="pi pi-check-circle text-green-500 text-3xl mb-2"></i>
                            <p class="text-green-700 dark:text-green-300 font-medium">El cliente no tiene facturas pendientes</p>
                        </div>
                    </div>

                    <Divider />

                    <!-- Contacts Section -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Emails -->
                        <div>
                            <div class="flex justify-between items-center mb-3">
                                <h3 class="text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                                    <i class="pi pi-envelope text-blue-500"></i>
                                    Correos Electrónicos
                                </h3>
                                <Button 
                                    label="Agregar" 
                                    icon="pi pi-plus"
                                    @click="showAddEmailDialog"
                                    class="p-button-success p-button-sm"
                                    :disabled="savingContact"
                                />
                            </div>
                            
                            <div v-if="clientDetails.client.emails.length === 0" class="text-center py-4 text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                                <i class="pi pi-inbox text-3xl mb-2"></i>
                                <p>No hay emails registrados</p>
                            </div>
                            <div v-else class="space-y-2">
                                <div 
                                    v-for="(email, index) in clientDetails.client.emails" 
                                    :key="index"
                                    class="flex items-center justify-between p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                                >
                                    <div class="flex items-center gap-2">
                                        <i class="pi pi-at text-blue-500"></i>
                                        <span class="text-zinc-900 dark:text-zinc-100">{{ email }}</span>
                                    </div>
                                    <Button 
                                        icon="pi pi-trash" 
                                        class="p-button-rounded p-button-danger p-button-text p-button-sm"
                                        @click="removeEmail(email)"
                                        title="Eliminar email"
                                        :disabled="savingContact"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Phones -->
                        <div>
                            <div class="flex justify-between items-center mb-3">
                                <h3 class="text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                                    <i class="pi pi-phone text-green-500"></i>
                                    Teléfonos
                                </h3>
                                <Button 
                                    label="Agregar" 
                                    icon="pi pi-plus"
                                    @click="showAddPhoneDialog"
                                    class="p-button-success p-button-sm"
                                    :disabled="savingContact"
                                />
                            </div>
                            
                            <div v-if="clientDetails.client.phones.length === 0" class="text-center py-4 text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                                <i class="pi pi-inbox text-3xl mb-2"></i>
                                <p>No hay teléfonos registrados</p>
                            </div>
                            <div v-else class="space-y-2">
                                <div 
                                    v-for="(phone, index) in clientDetails.client.phones" 
                                    :key="index"
                                    class="flex items-center justify-between p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                                >
                                    <div class="flex items-center gap-2">
                                        <i class="pi pi-mobile text-green-500"></i>
                                        <span class="text-zinc-900 dark:text-zinc-100">{{ phone }}</span>
                                    </div>
                                    <Button 
                                        icon="pi pi-trash" 
                                        class="p-button-rounded p-button-danger p-button-text p-button-sm"
                                        @click="removePhone(phone)"
                                        title="Eliminar teléfono"
                                        :disabled="savingContact"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>

        <!-- Add Email Dialog -->
        <Dialog 
            v-model:visible="addEmailDialogVisible" 
            modal 
            header="Agregar Email"
            :style="{width: '450px'}"
            :closable="!savingContact"
        >
            <div class="flex flex-col gap-4">
                <div>
                    <label for="newEmail" class="block text-sm font-medium mb-2">
                        Correo Electrónico
                    </label>
                    <InputText 
                        id="newEmail"
                        v-model="newEmail" 
                        placeholder="ejemplo@correo.com"
                        class="w-full"
                        type="email"
                        :disabled="savingContact"
                        @keyup.enter="addEmail"
                        autofocus
                    />
                </div>
            </div>
            <template #footer>
                <Button 
                    label="Cancelar" 
                    icon="pi pi-times" 
                    @click="addEmailDialogVisible = false"
                    class="p-button-text"
                    :disabled="savingContact"
                />
                <Button 
                    label="Agregar" 
                    icon="pi pi-check" 
                    @click="addEmail"
                    :disabled="savingContact || !newEmail.trim()"
                    :loading="savingContact"
                />
            </template>
        </Dialog>

        <!-- Add Phone Dialog -->
        <Dialog 
            v-model:visible="addPhoneDialogVisible" 
            modal 
            header="Agregar Teléfono"
            :style="{width: '450px'}"
            :closable="!savingContact"
        >
            <div class="flex flex-col gap-4">
                <div>
                    <label for="newPhone" class="block text-sm font-medium mb-2">
                        Número de Teléfono
                    </label>
                    <InputText 
                        id="newPhone"
                        v-model="newPhone" 
                        placeholder="1234567890"
                        class="w-full"
                        type="tel"
                        :disabled="savingContact"
                        @keyup.enter="addPhone"
                        autofocus
                    />
                </div>
            </div>
            <template #footer>
                <Button 
                    label="Cancelar" 
                    icon="pi pi-times" 
                    @click="addPhoneDialogVisible = false"
                    class="p-button-text"
                    :disabled="savingContact"
                />
                <Button 
                    label="Agregar" 
                    icon="pi pi-check" 
                    @click="addPhone"
                    :disabled="savingContact || !newPhone.trim()"
                    :loading="savingContact"
                />
            </template>
        </Dialog>

        <!-- Confirm Send Email Dialog -->
        <Dialog 
            v-model:visible="confirmSendEmailVisible" 
            modal 
            header="Confirmar Envío de Correo"
            :style="{width: '500px'}"
            :closable="!sendingEmails[selectedClientForEmail?.id]"
        >
            <div class="flex flex-col gap-4">
                <div class="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <i class="pi pi-exclamation-triangle text-amber-600 dark:text-amber-400 text-xl mt-1"></i>
                    <div>
                        <p class="font-medium text-amber-900 dark:text-amber-100 mb-1">¿Enviar notificación de adeudo?</p>
                        <p class="text-sm text-amber-700 dark:text-amber-300" v-if="selectedClientForEmail">
                            Se enviará correo a <strong>{{ selectedClientForEmail.emails.length }}</strong> email(s) registrado(s) para 
                            <strong>{{ selectedClientForEmail.name }}</strong> con un adeudo total de 
                            <strong>{{ selectedClientForEmail.total_debt }}</strong> en 
                            <strong>{{ selectedClientForEmail.invoices_count }}</strong> factura(s).
                        </p>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button 
                    label="Cancelar" 
                    icon="pi pi-times" 
                    @click="confirmSendEmailVisible = false"
                    class="p-button-text"
                    :disabled="sendingEmails[selectedClientForEmail?.id]"
                />
                <Button 
                    label="Enviar Correo" 
                    icon="pi pi-send" 
                    @click="sendDebtEmail"
                    :disabled="sendingEmails[selectedClientForEmail?.id]"
                    :loading="sendingEmails[selectedClientForEmail?.id]"
                />
            </template>
        </Dialog>

        <!-- WhatsApp Message Dialog -->
        <Dialog 
            v-model:visible="whatsappDialogVisible" 
            modal 
            header="Mensaje de WhatsApp"
            :style="{width: '600px'}"
        >
            <div class="flex flex-col gap-4">
                <div class="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                    <p class="text-sm text-zinc-600 dark:text-zinc-400 mb-2">Mensaje generado:</p>
                    <div class="p-3 bg-white dark:bg-zinc-900 rounded border border-zinc-200 dark:border-zinc-700 whitespace-pre-wrap font-mono text-sm">{{ whatsappMessage }}</div>
                </div>
                <div v-if="selectedClientForWhatsApp && selectedClientForWhatsApp.phones.length > 1">
                    <label class="block text-sm font-medium mb-2">Selecciona un número:</label>
                    <div class="space-y-2">
                        <div v-for="(phone, index) in selectedClientForWhatsApp.phones" :key="index" class="flex items-center gap-2">
                            <Button 
                                :label="phone"
                                icon="pi pi-whatsapp"
                                class="p-button-success w-full"
                                @click="openWhatsApp(phone)"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button 
                    label="Copiar al Portapapeles" 
                    icon="pi pi-copy" 
                    @click="copyWhatsAppMessage"
                    class="p-button-secondary"
                />
                <Button 
                    v-if="selectedClientForWhatsApp && selectedClientForWhatsApp.phones.length === 1"
                    label="Abrir WhatsApp" 
                    icon="pi pi-whatsapp" 
                    @click="openWhatsApp(selectedClientForWhatsApp.phones[0])"
                    class="p-button-success"
                />
            </template>
        </Dialog>
    </section>
</template>

<script>
import axiosInstance from '../axiosInstance';
import { FilterMatchMode } from 'primevue/api';

const TOAST_LIFETIMES = {
    SUCCESS: 3000,
    WARNING: 5000,
    ERROR: 5000
};

const TOAST_GROUP = 'topr';

const API_ENDPOINTS = {
    GET_CLIENTS_DEBTS_SUMMARY: '/api/get_clients_debts_summary',
    GET_CLIENT_DEBT_DETAILS: '/api/get_client_debt_details',
    SEND_DEBT_EMAIL: '/api/send_debt_email',
    ADD_EMAIL: '/api/add_client_debt_email',
    REMOVE_EMAIL: '/api/remove_client_debt_email',
    ADD_PHONE: '/api/add_client_debt_phone',
    REMOVE_PHONE: '/api/remove_client_debt_phone'
};

export default {
    name: 'CorreosAdeudos',
    emits: ['update-loading'],
    data() {
        return {
            clients: [],
            selectedClient: null,
            clientDetails: null,
            loadingClients: false,
            loadingDetails: false,
            savingContact: false,
            sendingEmails: {},
            showClientDetails: false,
            addEmailDialogVisible: false,
            addPhoneDialogVisible: false,
            confirmSendEmailVisible: false,
            whatsappDialogVisible: false,
            selectedClientForEmail: null,
            selectedClientForWhatsApp: null,
            whatsappMessage: '',
            newEmail: '',
            newPhone: '',
            filters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            }
        }
    },
    computed: {
        totalClients() {
            return this.clients.length;
        }
    },
    methods: {
        async fetchClientsSummary() {
            this.loadingClients = true;
            try {
                const response = await axiosInstance.post(API_ENDPOINTS.GET_CLIENTS_DEBTS_SUMMARY, {
                    token: localStorage.getItem('token')
                });

                if (response.data?.success) {
                    this.clients = response.data.clients || [];
                } else {
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema al obtener los clientes.', 
                        life: TOAST_LIFETIMES.WARNING, 
                        group: TOAST_GROUP 
                    });
                }
            } catch(error) {
                console.error("fetchClientsSummary: Error", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al obtener los clientes.', 
                    life: TOAST_LIFETIMES.ERROR, 
                    group: TOAST_GROUP 
                });
            } finally {
                this.loadingClients = false;
            }
        },

        async viewClientDetails(client) {
            this.selectedClient = client;
            this.showClientDetails = true;
            this.loadingDetails = true;

            try {
                const response = await axiosInstance.post(API_ENDPOINTS.GET_CLIENT_DEBT_DETAILS, {
                    token: localStorage.getItem('token'),
                    client_id: client.id
                });

                if (response.data?.success) {
                    this.clientDetails = response.data;
                } else {
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: 'Hubo un problema al obtener los detalles.', 
                        life: TOAST_LIFETIMES.WARNING, 
                        group: TOAST_GROUP 
                    });
                }
            } catch(error) {
                console.error("viewClientDetails: Error", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al obtener los detalles del cliente.', 
                    life: TOAST_LIFETIMES.ERROR, 
                    group: TOAST_GROUP 
                });
            } finally {
                this.loadingDetails = false;
            }
        },

        clearSelection() {
            this.showClientDetails = false;
            this.selectedClient = null;
            this.clientDetails = null;
            this.newEmail = '';
            this.newPhone = '';
        },

        confirmSendEmail(client) {
            this.selectedClientForEmail = client;
            this.confirmSendEmailVisible = true;
        },

        async sendDebtEmail() {
            if (!this.selectedClientForEmail) return;

            const clientId = this.selectedClientForEmail.id;
            this.sendingEmails[clientId] = true;

            try {
                const response = await axiosInstance.post(API_ENDPOINTS.SEND_DEBT_EMAIL, {
                    token: localStorage.getItem('token'),
                    client_id: clientId
                });

                if (response.data?.success) {
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: 'Éxito',
                        detail: `Correo enviado exitosamente a ${response.data.total_emails_sent} email(s)`, 
                        life: TOAST_LIFETIMES.SUCCESS, 
                        group: TOAST_GROUP 
                    });
                    
                    // Update only the specific client in the array to preserve sorting
                    const clientIndex = this.clients.findIndex(c => c.id === clientId);
                    if (clientIndex !== -1 && response.data.updated_client) {
                        this.clients[clientIndex] = {
                            ...this.clients[clientIndex],
                            emails_sent_count: response.data.updated_client.emails_sent_count,
                            last_email_sent: response.data.updated_client.last_email_sent
                        };
                    }
                    
                    this.confirmSendEmailVisible = false;
                    this.selectedClientForEmail = null;
                } else {
                    const errorMsg = response.data?.message || 'Hubo un problema al enviar el correo.';
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: errorMsg, 
                        life: TOAST_LIFETIMES.WARNING, 
                        group: TOAST_GROUP 
                    });
                }
            } catch(error) {
                console.error("sendDebtEmail: Error", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al enviar el correo.', 
                    life: TOAST_LIFETIMES.ERROR, 
                    group: TOAST_GROUP 
                });
            } finally {
                this.sendingEmails[clientId] = false;
            }
        },

        prepareWhatsAppMessage(client) {
            this.selectedClientForWhatsApp = client;
            
            // Generate WhatsApp message in frontend
            let message = `Hola, te contactamos de Vybroo.\n\n`;
            message += `Queremos recordarte que tienes ${client.invoices_count} factura(s) pendiente(s) `;
            message += `por un monto total de ${client.total_debt}.\n\n`;
            message += `Te pedimos por favor realizar el pago a la brevedad posible.\n\n`;
            message += `Gracias por tu atención.`;
            
            this.whatsappMessage = message;
            this.whatsappDialogVisible = true;
        },

        copyWhatsAppMessage() {
            navigator.clipboard.writeText(this.whatsappMessage).then(() => {
                this.$toast.add({ 
                    severity: 'success', 
                    summary: 'Copiado',
                    detail: 'Mensaje copiado al portapapeles', 
                    life: TOAST_LIFETIMES.SUCCESS, 
                    group: TOAST_GROUP 
                });
            });
        },

        openWhatsApp(phone) {
            const cleanPhone = phone.replace(/\D/g, '');
            const encodedMessage = encodeURIComponent(this.whatsappMessage);
            const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
            window.open(whatsappUrl, '_blank');
        },

        showAddEmailDialog() {
            this.newEmail = '';
            this.addEmailDialogVisible = true;
        },

        showAddPhoneDialog() {
            this.newPhone = '';
            this.addPhoneDialogVisible = true;
        },

        async addEmail() {
            if (!this.newEmail.trim() || !this.selectedClient) return;

            const email = this.newEmail.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                this.$toast.add({ 
                    severity: 'warn', 
                    summary: 'Aviso',
                    detail: 'Por favor, ingresa un email válido.', 
                    life: TOAST_LIFETIMES.WARNING, 
                    group: TOAST_GROUP 
                });
                return;
            }

            this.savingContact = true;
            try {
                const response = await axiosInstance.post(API_ENDPOINTS.ADD_EMAIL, {
                    token: localStorage.getItem('token'),
                    client_id: this.selectedClient.id,
                    email: email
                });

                if (response.data?.success) {
                    this.clientDetails.client.emails = response.data.client.debts_emails || [];
                    
                    // Update emails in main table without reloading
                    const clientIndex = this.clients.findIndex(c => c.id === this.selectedClient.id);
                    if (clientIndex !== -1) {
                        this.clients[clientIndex].emails = response.data.client.debts_emails || [];
                    }
                    
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: 'Éxito',
                        detail: 'Email agregado correctamente.', 
                        life: TOAST_LIFETIMES.SUCCESS, 
                        group: TOAST_GROUP 
                    });
                    
                    this.addEmailDialogVisible = false;
                    this.newEmail = '';
                } else {
                    const errorMsg = response.data?.message || response.data?.error || 'Hubo un problema al agregar el email.';
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: errorMsg, 
                        life: TOAST_LIFETIMES.WARNING, 
                        group: TOAST_GROUP 
                    });
                }
            } catch(error) {
                console.error("addEmail: Error", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al agregar el email.', 
                    life: TOAST_LIFETIMES.ERROR, 
                    group: TOAST_GROUP 
                });
            } finally {
                this.savingContact = false;
            }
        },

        removeEmail(email) {
            this.$confirm.require({
                header: 'Confirmación de eliminación',
                message: `¿Eliminar el email "${email}"?`,
                icon: 'pi pi-exclamation-triangle',
                rejectLabel: 'Cancelar',
                acceptLabel: 'Eliminar',
                rejectClass: 'p-button-secondary p-button-outlined',
                acceptClass: 'p-button-danger',
                accept: async () => {
                    this.savingContact = true;
                    try {
                        const response = await axiosInstance.post(API_ENDPOINTS.REMOVE_EMAIL, {
                            token: localStorage.getItem('token'),
                            client_id: this.selectedClient.id,
                            email: email
                        });

                        if (response.data?.success) {
                            this.clientDetails.client.emails = response.data.client.debts_emails || [];
                            
                            // Update emails in main table without reloading
                            const clientIndex = this.clients.findIndex(c => c.id === this.selectedClient.id);
                            if (clientIndex !== -1) {
                                this.clients[clientIndex].emails = response.data.client.debts_emails || [];
                            }
                            
                            this.$toast.add({ 
                                severity: 'success', 
                                summary: 'Éxito',
                                detail: 'Email eliminado correctamente.', 
                                life: TOAST_LIFETIMES.SUCCESS, 
                                group: TOAST_GROUP 
                            });
                        } else {
                            this.$toast.add({ 
                                severity: 'warn', 
                                summary: 'Aviso',
                                detail: 'Hubo un problema al eliminar el email.', 
                                life: TOAST_LIFETIMES.WARNING, 
                                group: TOAST_GROUP 
                            });
                        }
                    } catch(error) {
                        console.error("removeEmail: Error", error);
                        this.$toast.add({ 
                            severity: 'error', 
                            summary: 'Error',
                            detail: 'Error al eliminar el email.', 
                            life: TOAST_LIFETIMES.ERROR, 
                            group: TOAST_GROUP 
                        });
                    } finally {
                        this.savingContact = false;
                    }
                }
            });
        },

        async addPhone() {
            if (!this.newPhone.trim() || !this.selectedClient) return;

            const phone = this.newPhone.trim();

            this.savingContact = true;
            try {
                const response = await axiosInstance.post(API_ENDPOINTS.ADD_PHONE, {
                    token: localStorage.getItem('token'),
                    client_id: this.selectedClient.id,
                    phone: phone
                });

                if (response.data?.success) {
                    this.clientDetails.client.phones = response.data.client.debts_phone_numbers || [];
                    
                    // Update phones in main table without reloading
                    const clientIndex = this.clients.findIndex(c => c.id === this.selectedClient.id);
                    if (clientIndex !== -1) {
                        this.clients[clientIndex].phones = response.data.client.debts_phone_numbers || [];
                    }
                    
                    this.$toast.add({ 
                        severity: 'success', 
                        summary: 'Éxito',
                        detail: 'Teléfono agregado correctamente.', 
                        life: TOAST_LIFETIMES.SUCCESS, 
                        group: TOAST_GROUP 
                    });
                    
                    this.addPhoneDialogVisible = false;
                    this.newPhone = '';
                } else {
                    const errorMsg = response.data?.message || response.data?.error || 'Hubo un problema al agregar el teléfono.';
                    this.$toast.add({ 
                        severity: 'warn', 
                        summary: 'Aviso',
                        detail: errorMsg, 
                        life: TOAST_LIFETIMES.WARNING, 
                        group: TOAST_GROUP 
                    });
                }
            } catch(error) {
                console.error("addPhone: Error", error);
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error',
                    detail: 'Error al agregar el teléfono.', 
                    life: TOAST_LIFETIMES.ERROR, 
                    group: TOAST_GROUP 
                });
            } finally {
                this.savingContact = false;
            }
        },

        removePhone(phone) {
            this.$confirm.require({
                header: 'Confirmación de eliminación',
                message: `¿Eliminar el teléfono "${phone}"?`,
                icon: 'pi pi-exclamation-triangle',
                rejectLabel: 'Cancelar',
                acceptLabel: 'Eliminar',
                rejectClass: 'p-button-secondary p-button-outlined',
                acceptClass: 'p-button-danger',
                accept: async () => {
                    this.savingContact = true;
                    try {
                        const response = await axiosInstance.post(API_ENDPOINTS.REMOVE_PHONE, {
                            token: localStorage.getItem('token'),
                            client_id: this.selectedClient.id,
                            phone: phone
                        });

                        if (response.data?.success) {
                            this.clientDetails.client.phones = response.data.client.debts_phone_numbers || [];
                            
                            // Update phones in main table without reloading
                            const clientIndex = this.clients.findIndex(c => c.id === this.selectedClient.id);
                            if (clientIndex !== -1) {
                                this.clients[clientIndex].phones = response.data.client.debts_phone_numbers || [];
                            }
                            
                            this.$toast.add({ 
                                severity: 'success', 
                                summary: 'Éxito',
                                detail: 'Teléfono eliminado correctamente.', 
                                life: TOAST_LIFETIMES.SUCCESS, 
                                group: TOAST_GROUP 
                            });
                        } else {
                            this.$toast.add({ 
                                severity: 'warn', 
                                summary: 'Aviso',
                                detail: 'Hubo un problema al eliminar el teléfono.', 
                                life: TOAST_LIFETIMES.WARNING, 
                                group: TOAST_GROUP 
                            });
                        }
                    } catch(error) {
                        console.error("removePhone: Error", error);
                        this.$toast.add({ 
                            severity: 'error', 
                            summary: 'Error',
                            detail: 'Error al eliminar el teléfono.', 
                            life: TOAST_LIFETIMES.ERROR, 
                            group: TOAST_GROUP 
                        });
                    } finally {
                        this.savingContact = false;
                    }
                }
            });
        },

        formatDate(dateString) {
            if (!dateString) return 'Nunca';
            const date = new Date(dateString);
            return date.toLocaleDateString('es-MX', { year: 'numeric', month: '2-digit', day: '2-digit' });
        }
    },
    mounted() {
        this.$emit("update-loading", true);
        this.fetchClientsSummary().finally(() => {
            this.$emit("update-loading", false);
        });
    }
}
</script>

<style scoped>
.pi-inbox {
    display: block;
}

/* Responsive container for horizontal scrolling */
.table-responsive-container {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0;
    padding: 0;
    min-width: 0;
    scroll-behavior: smooth;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
}

/* Dark mode scrollbar */
.dark .table-responsive-container {
    scrollbar-color: #4b5563 #1f2937;
}

/* Allow horizontal scroll inside wrapper */
.table-responsive-container ::v-deep(.p-datatable-wrapper) {
    border-radius: 0.5rem;
    overflow-x: auto;
}

/* Custom scrollbar for webkit browsers */
.table-responsive-container::-webkit-scrollbar {
    height: 8px;
}

.table-responsive-container::-webkit-scrollbar-track {
    @apply bg-gray-100 dark:bg-gray-700 rounded;
}

.table-responsive-container::-webkit-scrollbar-thumb {
    @apply bg-gray-400 dark:bg-gray-500 rounded;
}

.table-responsive-container::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-500 dark:bg-gray-400;
}

/* DataTable base styles */
::v-deep(.debts-summary-table.p-datatable) {
    border-radius: 0.5rem !important;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important;
    min-width: 1100px; /* Force horizontal scroll on smaller screens */
    border: none !important;
}

::v-deep(.debts-summary-table.p-datatable .p-datatable-wrapper) {
    border-radius: 0.5rem !important;
    overflow-x: auto !important;
}

/* Light mode - Header */
::v-deep(.debts-summary-table.p-datatable .p-datatable-thead > tr > th) {
    background-color: #0f1338 !important;
    color: white !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    padding: 0.75rem 1rem !important;
    border: none !important;
    white-space: nowrap;
    line-height: 1 !important;
}

@media (min-width: 640px) {
    ::v-deep(.debts-summary-table.p-datatable .p-datatable-thead > tr > th) {
        font-size: 1rem !important;
    }
}

/* Light mode - Body rows */
::v-deep(.debts-summary-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td) {
    background-color: #f3f4f6 !important;
    color: #111827 !important;
}

::v-deep(.debts-summary-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td) {
    background-color: #ffffff !important;
    color: #111827 !important;
}

::v-deep(.debts-summary-table.p-datatable .p-datatable-tbody > tr > td) {
    padding: 0.75rem 1rem !important;
    border-bottom: 1px solid #e5e7eb !important;
    vertical-align: top !important;
    border: none !important;
}

@media (min-width: 640px) {
    ::v-deep(.debts-summary-table.p-datatable .p-datatable-tbody > tr > td) {
        padding: 0.75rem 1rem !important;
    }
}

/* Light mode - Hover */
::v-deep(.debts-summary-table.p-datatable .p-datatable-tbody > tr) {
    cursor: pointer !important;
    transition: background-color 0.3s ease !important;
}

::v-deep(.debts-summary-table.p-datatable .p-datatable-tbody > tr:hover > td) {
    background-color: #fef3c7 !important;
    color: #111827 !important;
}

/* Button sizing in table */
::v-deep(.debts-summary-table .p-button-sm) {
    padding: 0.35rem 0.5rem !important;
    font-size: 0.75rem !important;
}

@media (min-width: 640px) {
    ::v-deep(.debts-summary-table .p-button-sm) {
        padding: 0.5rem 0.75rem !important;
        font-size: 0.875rem !important;
    }
}
</style>

<style>
/* Dark mode - Header */
.dark .debts-summary-table.p-datatable .p-datatable-thead > tr > th {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

/* Dark mode - Body base */
.dark .debts-summary-table.p-datatable .p-datatable-tbody > tr > td {
    color: white !important;
}

/* Dark mode - Odd rows */
.dark .debts-summary-table.p-datatable .p-datatable-tbody > tr:nth-child(odd) > td {
    background-color: #18181b !important;
    color: white !important;
}

/* Dark mode - Even rows */
.dark .debts-summary-table.p-datatable .p-datatable-tbody > tr:nth-child(even) > td {
    background-color: #27272a !important;
    color: white !important;
}

/* Dark mode - Hover */
.dark .debts-summary-table.p-datatable .p-datatable-tbody > tr:hover > td {
    background-color: #422006 !important;
    color: white !important;
}

/* Dark mode - Paginator */
.dark .debts-summary-table.p-datatable .p-paginator {
    background-color: #27272a !important;
    border-top: 1px solid #3f3f46 !important;
}

.dark .debts-summary-table.p-datatable .p-paginator-bottom {
    border: none !important;
    
}

.dark .debts-summary-table.p-datatable .p-paginator .p-paginator-pages .p-paginator-page {
    color: #d1d5db !important;
}

.dark .debts-summary-table.p-datatable .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

/* Prevent header background change on active sort - Light mode */
.debts-summary-table.p-datatable .p-datatable-thead > tr > th.p-highlight {
    background-color: #0f1338 !important;
    color: white !important;
}

/* Prevent header background change on active sort - Dark mode */
.dark .debts-summary-table.p-datatable .p-datatable-thead > tr > th.p-highlight {
    background-color: #D9D9D9 !important;
    color: #0f1338 !important;
}

/* Sort icon contrast in dark mode */
.dark .debts-summary-table.p-datatable .p-datatable-thead > tr > th .p-sortable-column-icon {
    color: #0f1338 !important;
}

/* Active sort indicator in dark mode */
.dark .debts-summary-table.p-datatable .p-datatable-thead > tr > th.p-highlight .p-sortable-column-icon {
    color: #3b82f6 !important;
}

/* Remove background from search input */
.debts-summary-table.p-datatable .p-datatable-header {
    background: transparent !important;
    border: none !important;
}
</style>
